import { useState } from 'react';

import { getCheckoutUrls, TOKEN_PACKS, useCreateCheckout } from '../hooks/useCheckout';
import { TokenBalance, useTokenHistory } from '../hooks/useTokens';

interface TokensCardProps {
  balance?: TokenBalance;
}

export default function TokensCard({ balance }: TokensCardProps) {
  const [showHistory, setShowHistory] = useState(false);
  const { data: historyData, fetchNextPage, hasNextPage, isFetchingNextPage } = useTokenHistory();
  const createCheckout = useCreateCheckout();
  const checkoutUrls = getCheckoutUrls();

  // Only show grants and refunds, not individual spend transactions (reading chapters)
  const transactions =
    historyData?.pages
      .flatMap((page) => page.transactions)
      .filter((tx) => tx.type === 'grant' || tx.type === 'refund') ?? [];

  const handleBuyPack = (packId: 'small' | 'medium') => {
    createCheckout.mutate({
      productType: 'pack',
      productId: packId,
      ...checkoutUrls,
    });
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <svg
          className="w-5 h-5 text-purple-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        Tokens
      </h2>

      {/* Balance display */}
      <div className="text-center py-6 bg-purple-600/10 rounded-xl mb-6">
        <div className="text-5xl font-bold text-white mb-1">{balance?.balance ?? 0}</div>
        <div className="text-purple-300">tokens available</div>
        {/* Token breakdown */}
        <div className="flex justify-center gap-4 mt-2 text-sm">
          <span className="text-green-400">{balance?.purchasedTokens ?? 0} purchased</span>
          <span className="text-purple-400">{balance?.subscriptionTokens ?? 0} subscription</span>
        </div>
      </div>

      {/* Buy tokens section */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-purple-300 mb-3">Buy Token Packs</h3>
        <div className="grid grid-cols-2 gap-3">
          {TOKEN_PACKS.map((pack) => (
            <button
              key={pack.id}
              onClick={() => handleBuyPack(pack.id)}
              disabled={createCheckout.isPending}
              className="p-4 bg-slate-700/50 hover:bg-slate-700 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all text-left disabled:opacity-50"
            >
              <div className="text-lg font-semibold text-white">{pack.tokens} tokens</div>
              <div className="text-purple-300">{pack.price}</div>
            </button>
          ))}
        </div>
        {createCheckout.isPending && (
          <p className="text-sm text-purple-300 mt-2 text-center">Redirecting to checkout...</p>
        )}
      </div>

      {/* Transaction history toggle */}
      <button
        onClick={() => setShowHistory(!showHistory)}
        className="w-full flex items-center justify-between text-sm text-purple-400 hover:text-purple-300 transition-colors"
      >
        <span>Transaction history</span>
        <svg
          className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Transaction history list */}
      {showHistory && (
        <div className="mt-4 space-y-2">
          {transactions.length === 0 && !hasNextPage ? (
            <p className="text-sm text-purple-300/60 text-center py-4">No transactions yet</p>
          ) : (
            <>
              {transactions.map((tx) => (
                <div
                  key={tx.transactionId}
                  className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                        tx.type === 'grant'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}
                    >
                      {tx.type === 'grant' ? '+' : 'R'}
                    </span>
                    <div>
                      <div className="text-sm text-white">{tx.description}</div>
                      <div className="text-xs text-purple-400">
                        {new Date(tx.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <span className="font-medium text-green-400">+{Math.abs(tx.amount)}</span>
                </div>
              ))}

              {hasNextPage && (
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="w-full py-2 text-sm text-purple-400 hover:text-purple-300 transition-colors disabled:opacity-50"
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load more'}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
